import dynamic from "next/dynamic";
import { JSX, Suspense } from "react";
import { Components } from "@/app/(components)/_components/Components";
import type { ConfirmDialogProps } from "@/app/(components)/generics/ConfirmDialog/ConfirmDialog";
import ModalLoader from "@/app/(components)/generics/loaders/ModalLoader/ModalLoader";
import type { AddAffiliationProps } from "@/app/(components)/specifics/administration/Affiliation/components/AddAffiliation/AddAffiliation";
import type { AffiliationPayoutProps } from "@/app/(components)/specifics/administration/Affiliation/components/AffiliationPayout/AffiliationPayout";
import type { EditAffiliationProps } from "@/app/(components)/specifics/administration/Affiliation/components/EditAffiliation/EditAffiliation";
import type { CompanyDeletionProps } from "@/app/(components)/specifics/administration/Companies/components/CompanyDeletion/CompanyDeletion";
import type { CompanyFormProps } from "@/app/(components)/specifics/administration/Companies/components/CompanyForm/CompanyForm";
import type { CompanyMemberFormProps } from "@/app/(components)/specifics/administration/Companies/components/CompanyMemberForm/CompanyMemberForm";
import type { AdminUserProfileProps } from "@/app/(components)/specifics/administration/Users/components/AdminUserProfile/AdminUserProfile";
import type { UserAffiliationProps } from "@/app/(components)/specifics/affiliation/UserAffiliation/UserAffiliation";
import type { ChangeEmailProps } from "@/app/(components)/specifics/auth/ChangeEmail/ChangeEmail";
import type { ResetPasswordProps } from "@/app/(components)/specifics/auth/ResetPassword/ResetPassword";
import type { SignUpProps } from "@/app/(components)/specifics/auth/SignUp/SignUp";
import type { BillingProps } from "@/app/(components)/specifics/billing/Billing/Billing";
import type { CostConfirmProps } from "@/app/(components)/specifics/billing/CostConfirm/CostConfirm";
import type { MyCompanyMemberFormProps } from "@/app/(components)/specifics/company/MyCompanyMemberForm/MyCompanyMemberForm";
import type { JobFailureProps } from "@/app/(components)/specifics/jobs/JobFailure/JobFailure";
import type { AddCallTypeProps } from "@/app/(components)/specifics/projet/callType/AddCallType/AddCallType";
import type { DeleteCallTypeProps } from "@/app/(components)/specifics/projet/callType/DeleteCallType/DeleteCallType";
import type { EditCallTypeProps } from "@/app/(components)/specifics/projet/callType/EditCallType/EditCallType";
import type { AddProjectMemberProps } from "@/app/(components)/specifics/projet/member/AddProjectMember/AddProjectMember";
import type { DeleteProjectMemberProps } from "@/app/(components)/specifics/projet/member/DeleteProjectMember/DeleteProjectMember";
import type { ProjectDeletionProps } from "@/app/(components)/specifics/projet/ProjectDeletion/ProjectDeletion";
import type { ProjectFormProps } from "@/app/(components)/specifics/projet/ProjectForm/ProjectForm";
import type { AnalysisInsightsProps } from "@/app/(components)/specifics/tools/CallAnalysis/components/Analysis/components/AnalysisInsights/AnalysisInsights";
import type { AnalysisRecommendationDetailProps } from "@/app/(components)/specifics/tools/CallAnalysis/components/Analysis/components/AnalysisRecommendations/components/AnalysisRecommendationDetail/AnalysisRecommendationDetail";
import type { CallDetailProps } from "@/app/(components)/specifics/tools/CallAnalysis/components/CallDetail/CallDetail";
import type { InsightCallsProps } from "@/app/(components)/specifics/tools/CallAnalysis/components/InsightCalls/InsightCalls";
import type { ImportLinkModalProps } from "@/app/(components)/specifics/tools/CallAnalysis/components/UploadCalls/components/ImportLinkModal/ImportLinkModal";
import type { VideoAudioExtractProps } from "@/app/(components)/specifics/tools/CallAnalysis/components/VideoAudioExtract/VideoAudioExtract";
import type { AvatarExtractionProps } from "@/app/(components)/specifics/tools/ClientAvatar/components/AvatarExtraction/AvatarExtraction";
import type { ScriptInsightQuotesProps } from "@/app/(components)/specifics/tools/MessagesContent/components/Scripts/components/ScriptInsightQuotes/ScriptInsightQuotes";
import type { SequenceGenerateProps } from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SequenceGenerate/SequenceGenerate";
import type { SequenceObjectiveFormProps } from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SequenceObjectiveForm/SequenceObjectiveForm";
import type { SequenceStepFormProps } from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SequenceStepForm/SequenceStepForm";
import type { SequenceStepRegenerateProps } from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SequenceStepRegenerate/SequenceStepRegenerate";
import type { SequenceVariantFormProps } from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SequenceVariantForm/SequenceVariantForm";
import type { OfferDeletionProps } from "@/app/(components)/specifics/tools/Offers/components/OfferDeletion/OfferDeletion";
import type { OfferFormProps } from "@/app/(components)/specifics/tools/Offers/components/OfferForm/OfferForm";
import type { ImportedBaseModalProps } from "@/app/(components)/specifics/tools/ProspectSearch/components/ImportedBaseModal/ImportedBaseModal";
import type { ImportedProfileExtensionGuideProps } from "@/app/(components)/specifics/tools/ProspectSearch/components/ImportedProfileExtensionGuide/ImportedProfileExtensionGuide";
import type { ImportedProfileRejectReasonProps } from "@/app/(components)/specifics/tools/ProspectSearch/components/ImportedProfileRejectReason/ImportedProfileRejectReason";
import type { ImportedProfilesTabsProps } from "@/app/(components)/specifics/tools/ProspectSearch/components/ImportedProfilesTabs/ImportedProfilesTabs";
import type { ProspectActionConfirmProps } from "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectActions/ProspectActionConfirm";
import type { ProspectActionTypeFormProps } from "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectActions/ProspectActionTypeForm";
import type { ProspectDetailProps } from "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectDetail/ProspectDetail";
import type { ProspectFormProps } from "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectForm/ProspectForm";
import type { ProspectRejectReasonProps } from "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectRejectReason/ProspectRejectReason";
import type { ProspectsFoundProps } from "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectsFound/ProspectsFound";
import { NotFoundError } from "@/app/errors/NotFoundError";
import type { AvatarDetailProps } from "@/app/types/client-avatar/AvatarDetailProps";
import type { ForgotPasswordProps } from "../specifics/auth/ForgotPassword/ForgotPassword";
import type { SignInProps } from "../specifics/auth/SignIn/SignIn";
import type { SettingsProps } from "../specifics/settings/Settings/Settings";
import type { ExpertAuditConfirmProps } from "../specifics/tools/CallAnalysis/components/Analysis/components/AnalysisRecommendations/components/ExpertAuditConfirm/ExpertAuditConfirm";
import type { ConfirmAutoRunProps } from "../specifics/tools/CallAnalysis/components/Calls/components/ConfirmAutoRun/ConfirmAutoRun";
import type { ExportCallProps } from "../specifics/tools/CallAnalysis/components/ExportCall/ExportCall";
import type { ExportInsightProps } from "../specifics/tools/CallAnalysis/components/ExportInsight/ExportInsight";
import type { ExportAvatarProps } from "../specifics/tools/ClientAvatar/components/ExportAvatar/ExportAvatar";

// Lazy-loaded so a page mounting <Modal/> does not pull every modal's tree into its
// compile graph: each modal is compiled and its chunk fetched only when it opens.
const CalendlyBooking = dynamic(
  () => import("@/app/(components)/generics/CalendlyBooking/CalendlyBooking"),
);
const AffiliationPayout = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/administration/Affiliation/components/AffiliationPayout/AffiliationPayout"
    ),
);
const AddAffiliation = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/administration/Affiliation/components/AddAffiliation/AddAffiliation"
    ),
);
const EditAffiliation = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/administration/Affiliation/components/EditAffiliation/EditAffiliation"
    ),
);
const CompanyForm = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/administration/Companies/components/CompanyForm/CompanyForm"
    ),
);
const CompanyDeletion = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/administration/Companies/components/CompanyDeletion/CompanyDeletion"
    ),
);
const CompanyMemberForm = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/administration/Companies/components/CompanyMemberForm/CompanyMemberForm"
    ),
);
const MyCompanyMemberForm = dynamic(
  () => import("@/app/(components)/specifics/company/MyCompanyMemberForm/MyCompanyMemberForm"),
);
const UserAffiliation = dynamic(
  () => import("@/app/(components)/specifics/affiliation/UserAffiliation/UserAffiliation"),
);
const ConfirmDialog = dynamic(
  () => import("@/app/(components)/generics/ConfirmDialog/ConfirmDialog"),
);
const ProspectDetail = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectDetail/ProspectDetail"
    ),
);
const ChangeEmail = dynamic(
  () => import("@/app/(components)/specifics/auth/ChangeEmail/ChangeEmail"),
);
const ResetPassword = dynamic(
  () => import("@/app/(components)/specifics/auth/ResetPassword/ResetPassword"),
);
const SignUp = dynamic(() => import("@/app/(components)/specifics/auth/SignUp/SignUp"));
const Billing = dynamic(() => import("@/app/(components)/specifics/billing/Billing/Billing"));
const JobFailure = dynamic(() => import("@/app/(components)/specifics/jobs/JobFailure/JobFailure"));
const AddCallType = dynamic(
  () => import("@/app/(components)/specifics/projet/callType/AddCallType/AddCallType"),
);
const ScriptInsightQuotes = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/MessagesContent/components/Scripts/components/ScriptInsightQuotes/ScriptInsightQuotes"
    ),
);
const DeleteCallType = dynamic(
  () => import("@/app/(components)/specifics/projet/callType/DeleteCallType/DeleteCallType"),
);
const EditCallType = dynamic(
  () => import("@/app/(components)/specifics/projet/callType/EditCallType/EditCallType"),
);
const AddProjectMember = dynamic(
  () => import("@/app/(components)/specifics/projet/member/AddProjectMember/AddProjectMember"),
);
const DeleteProjectMember = dynamic(
  () =>
    import("@/app/(components)/specifics/projet/member/DeleteProjectMember/DeleteProjectMember"),
);
const OfferDeletion = dynamic(
  () => import("@/app/(components)/specifics/tools/Offers/components/OfferDeletion/OfferDeletion"),
);
const OfferForm = dynamic(
  () => import("@/app/(components)/specifics/tools/Offers/components/OfferForm/OfferForm"),
);
const SequenceStepForm = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SequenceStepForm/SequenceStepForm"
    ),
);
const SequenceGenerate = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SequenceGenerate/SequenceGenerate"
    ),
);
const SequenceObjectiveForm = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SequenceObjectiveForm/SequenceObjectiveForm"
    ),
);
const SequenceVariantForm = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SequenceVariantForm/SequenceVariantForm"
    ),
);
const SequenceStepRegenerate = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SequenceStepRegenerate/SequenceStepRegenerate"
    ),
);
const ProjectDeletion = dynamic(
  () => import("@/app/(components)/specifics/projet/ProjectDeletion/ProjectDeletion"),
);
const ProjectForm = dynamic(
  () => import("@/app/(components)/specifics/projet/ProjectForm/ProjectForm"),
);
const AnalysisInsights = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/CallAnalysis/components/Analysis/components/AnalysisInsights/AnalysisInsights"
    ),
  { loading: ModalLoader },
);
const AnalysisRecommendationDetail = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/CallAnalysis/components/Analysis/components/AnalysisRecommendations/components/AnalysisRecommendationDetail/AnalysisRecommendationDetail"
    ),
);
const CallDetail = dynamic(
  () => import("@/app/(components)/specifics/tools/CallAnalysis/components/CallDetail/CallDetail"),
);
const InsightCalls = dynamic(
  () =>
    import("@/app/(components)/specifics/tools/CallAnalysis/components/InsightCalls/InsightCalls"),
  { loading: ModalLoader },
);
const AdminUserProfile = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/administration/Users/components/AdminUserProfile/AdminUserProfile"
    ),
);
const ImportLinkModal = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/CallAnalysis/components/UploadCalls/components/ImportLinkModal/ImportLinkModal"
    ),
);
const VideoAudioExtract = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/CallAnalysis/components/VideoAudioExtract/VideoAudioExtract"
    ),
);
const CostConfirm = dynamic(
  () => import("@/app/(components)/specifics/billing/CostConfirm/CostConfirm"),
);
const ImportedBaseModal = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ProspectSearch/components/ImportedBaseModal/ImportedBaseModal"
    ),
);
const AvatarDetail = dynamic(
  () =>
    import("@/app/(components)/specifics/tools/ClientAvatar/components/AvatarDetail/AvatarDetail"),
);
const AvatarExtraction = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ClientAvatar/components/AvatarExtraction/AvatarExtraction"
    ),
);
const ImportedProfileExtensionGuide = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ProspectSearch/components/ImportedProfileExtensionGuide/ImportedProfileExtensionGuide"
    ),
);
const ImportedProfilesTabs = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ProspectSearch/components/ImportedProfilesTabs/ImportedProfilesTabs"
    ),
);
const ImportedProfileRejectReason = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ProspectSearch/components/ImportedProfileRejectReason/ImportedProfileRejectReason"
    ),
);
const ProspectsFound = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectsFound/ProspectsFound"
    ),
);
const ProspectForm = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectForm/ProspectForm"
    ),
);
const ProspectRejectReason = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectRejectReason/ProspectRejectReason"
    ),
);
const ProspectActionConfirm = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectActions/ProspectActionConfirm"
    ),
);
const ProspectActionTypeForm = dynamic(
  () =>
    import(
      "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectActions/ProspectActionTypeForm"
    ),
);
const ForgotPassword = dynamic(() => import("../specifics/auth/ForgotPassword/ForgotPassword"));
const SignIn = dynamic(() => import("../specifics/auth/SignIn/SignIn"));
const Settings = dynamic(() => import("../specifics/settings/Settings/Settings"));
const ConfirmAutoRun = dynamic(
  () =>
    import(
      "../specifics/tools/CallAnalysis/components/Calls/components/ConfirmAutoRun/ConfirmAutoRun"
    ),
);
const ExpertAuditConfirm = dynamic(
  () =>
    import(
      "../specifics/tools/CallAnalysis/components/Analysis/components/AnalysisRecommendations/components/ExpertAuditConfirm/ExpertAuditConfirm"
    ),
);
const ExportCall = dynamic(
  () => import("../specifics/tools/CallAnalysis/components/ExportCall/ExportCall"),
);
const ExportInsight = dynamic(
  () => import("../specifics/tools/CallAnalysis/components/ExportInsight/ExportInsight"),
);
const ExportAvatar = dynamic(
  () => import("../specifics/tools/ClientAvatar/components/ExportAvatar/ExportAvatar"),
);

export class ComponentFactory {
  public static create(
    componentName: Components,
    props?: Record<string, unknown>,
  ): JSX.Element | null {
    let component: null | JSX.Element = null;
    const _props = props || {};

    switch (componentName) {
      case Components.ResetPassword:
        component = <ResetPassword {...(_props as unknown as ResetPasswordProps)} />;
        break;
      case Components.SignUp:
        component = <SignUp {...(_props as unknown as SignUpProps)} />;
        break;
      case Components.SignIn:
        component = <SignIn {...(_props as unknown as SignInProps)} />;
        break;
      case Components.ForgotPassword:
        component = <ForgotPassword {...(_props as unknown as ForgotPasswordProps)} />;
        break;
      case Components.Settings:
        component = <Settings {...(_props as unknown as SettingsProps)} />;
        break;
      case Components.ChangeEmail:
        component = <ChangeEmail {...(_props as unknown as ChangeEmailProps)} />;
        break;
      case Components.CallDetail:
        component = <CallDetail {...(_props as unknown as CallDetailProps)} />;
        break;
      case Components.InsightCalls:
        component = <InsightCalls {...(_props as unknown as InsightCallsProps)} />;
        break;
      case Components.AdminUserProfile:
        component = <AdminUserProfile {...(_props as unknown as AdminUserProfileProps)} />;
        break;
      case Components.Billing:
        component = <Billing {...(_props as unknown as BillingProps)} />;
        break;
      case Components.ProjectForm:
        component = <ProjectForm {...(_props as unknown as ProjectFormProps)} />;
        break;
      case Components.AnalysisInsights:
        component = <AnalysisInsights {...(_props as unknown as AnalysisInsightsProps)} />;
        break;
      case Components.AnalysisRecommendationDetail:
        component = (
          <AnalysisRecommendationDetail
            {...(_props as unknown as AnalysisRecommendationDetailProps)}
          />
        );
        break;
      case Components.ImportLinkModal:
        component = <ImportLinkModal {...(_props as unknown as ImportLinkModalProps)} />;
        break;
      case Components.VideoAudioExtract:
        component = <VideoAudioExtract {...(_props as unknown as VideoAudioExtractProps)} />;
        break;
      case Components.CostConfirm:
        component = <CostConfirm {...(_props as unknown as CostConfirmProps)} />;
        break;
      case Components.ImportedBase:
        component = <ImportedBaseModal {...(_props as unknown as ImportedBaseModalProps)} />;
        break;
      case Components.AvatarDetail:
        component = <AvatarDetail {...(_props as unknown as AvatarDetailProps)} />;
        break;
      case Components.AvatarExtraction:
        component = <AvatarExtraction {...(_props as unknown as AvatarExtractionProps)} />;
        break;

      case Components.OfferForm:
        component = <OfferForm {...(_props as unknown as OfferFormProps)} />;
        break;
      case Components.SequenceStepForm:
        component = <SequenceStepForm {...(_props as unknown as SequenceStepFormProps)} />;
        break;
      case Components.SequenceGenerate:
        component = <SequenceGenerate {...(_props as unknown as SequenceGenerateProps)} />;
        break;
      case Components.SequenceObjectiveForm:
        component = (
          <SequenceObjectiveForm {...(_props as unknown as SequenceObjectiveFormProps)} />
        );
        break;
      case Components.SequenceVariantForm:
        component = <SequenceVariantForm {...(_props as unknown as SequenceVariantFormProps)} />;
        break;
      case Components.SequenceStepRegenerate:
        component = (
          <SequenceStepRegenerate {...(_props as unknown as SequenceStepRegenerateProps)} />
        );
        break;
      case Components.AddProjectMember:
        component = <AddProjectMember {...(_props as unknown as AddProjectMemberProps)} />;
        break;
      case Components.DeleteProjectMember:
        component = <DeleteProjectMember {...(_props as unknown as DeleteProjectMemberProps)} />;
        break;
      case Components.ScriptInsightQuotes:
        component = <ScriptInsightQuotes {...(_props as unknown as ScriptInsightQuotesProps)} />;
        break;
      case Components.AddCallType:
        component = <AddCallType {...(_props as unknown as AddCallTypeProps)} />;
        break;
      case Components.EditCallType:
        component = <EditCallType {...(_props as unknown as EditCallTypeProps)} />;
        break;
      case Components.DeleteCallType:
        component = <DeleteCallType {...(_props as unknown as DeleteCallTypeProps)} />;
        break;
      case Components.OfferDeletion:
        component = <OfferDeletion {...(_props as unknown as OfferDeletionProps)} />;
        break;
      case Components.UserAffiliation:
        component = <UserAffiliation {...(_props as unknown as UserAffiliationProps)} />;
        break;
      case Components.AffiliationPayout:
        component = <AffiliationPayout {...(_props as unknown as AffiliationPayoutProps)} />;
        break;
      case Components.AddAffiliation:
        component = <AddAffiliation {...(_props as unknown as AddAffiliationProps)} />;
        break;
      case Components.EditAffiliation:
        component = <EditAffiliation {...(_props as unknown as EditAffiliationProps)} />;
        break;
      case Components.CompanyForm:
        component = <CompanyForm {...(_props as unknown as CompanyFormProps)} />;
        break;
      case Components.CompanyDeletion:
        component = <CompanyDeletion {...(_props as unknown as CompanyDeletionProps)} />;
        break;
      case Components.CompanyMemberForm:
        component = <CompanyMemberForm {...(_props as unknown as CompanyMemberFormProps)} />;
        break;
      case Components.MyCompanyMemberForm:
        component = <MyCompanyMemberForm {...(_props as unknown as MyCompanyMemberFormProps)} />;
        break;
      case Components.ProjectDeletion:
        component = <ProjectDeletion {...(_props as unknown as ProjectDeletionProps)} />;
        break;
      case Components.CallAnalysisAutoRunConfirm:
        component = <ConfirmAutoRun {...(_props as unknown as ConfirmAutoRunProps)} />;
        break;
      case Components.ExpertAuditConfirm:
        component = <ExpertAuditConfirm {...(_props as unknown as ExpertAuditConfirmProps)} />;
        break;
      case Components.ExportCall:
        component = <ExportCall {...(_props as unknown as ExportCallProps)} />;
        break;
      case Components.ExportInsight:
        component = <ExportInsight {...(_props as unknown as ExportInsightProps)} />;
        break;
      case Components.ExportAvatar:
        component = <ExportAvatar {...(_props as unknown as ExportAvatarProps)} />;
        break;
      case Components.JobFailure:
        component = <JobFailure {...(_props as unknown as JobFailureProps)} />;
        break;
      case Components.ImportedProfileExtensionGuide:
        component = (
          <ImportedProfileExtensionGuide
            {...(_props as unknown as ImportedProfileExtensionGuideProps)}
          />
        );
        break;
      case Components.ImportedProfilesTabs:
        component = <ImportedProfilesTabs {...(_props as unknown as ImportedProfilesTabsProps)} />;
        break;
      case Components.ProspectsFound:
        component = <ProspectsFound {...(_props as unknown as ProspectsFoundProps)} />;
        break;
      case Components.ImportedProfileRejectReason:
        component = (
          <ImportedProfileRejectReason
            {...(_props as unknown as ImportedProfileRejectReasonProps)}
          />
        );
        break;
      case Components.ProspectForm:
        component = <ProspectForm {...(_props as unknown as ProspectFormProps)} />;
        break;
      case Components.ConfirmDialog:
        component = <ConfirmDialog {...(_props as unknown as ConfirmDialogProps)} />;
        break;
      case Components.ProspectDetail:
        component = <ProspectDetail {...(_props as unknown as ProspectDetailProps)} />;
        break;
      case Components.ProspectReject:
        component = <ProspectRejectReason {...(_props as unknown as ProspectRejectReasonProps)} />;
        break;
      case Components.ProspectActionConfirm:
        component = (
          <ProspectActionConfirm {...(_props as unknown as ProspectActionConfirmProps)} />
        );
        break;
      case Components.ProspectActionType:
        component = (
          <ProspectActionTypeForm {...(_props as unknown as ProspectActionTypeFormProps)} />
        );
        break;
      case Components.CalendlyBooking:
        component = <CalendlyBooking {...(_props as unknown as { url: string })} />;
        break;
      default:
        throw new NotFoundError(`Component ${componentName}`);
    }

    return component && <Suspense fallback={<ModalLoader />}>{component}</Suspense>;
  }
}
